import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from '../../../components/AppIcon';
import MenuNodeRow from './MenuNodeRow';

// Flatten tree to find siblings of a given node
const flattenSiblings = (tree, parentId) => {
  if (parentId === null) return tree;
  const findChildren = (nodes) => {
    for (const n of nodes) {
      if (n.id === parentId) return n.children || [];
      const found = findChildren(n.children || []);
      if (found) return found;
    }
    return null;
  };
  return findChildren(tree) || [];
};

const MenuTreeEditor = ({ tree, companyId, savingIds, onAdd, onRename, onMoveUp, onMoveDown, onDelete }) => {
  const { t } = useTranslation();
  const [expandedIds, setExpandedIds] = useState(new Set());

  const handleToggleExpand = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const renderNodes = (nodes, parentId = null) => {
    return nodes.map((node, idx) => {
      const siblings = flattenSiblings(tree, parentId);
      return (
        <MenuNodeRow
          key={node.id}
          node={node}
          depth={node.id ? getDepth(tree, node.id) : 0}
          isFirst={idx === 0}
          isLast={idx === siblings.length - 1}
          isExpanded={expandedIds.has(node.id)}
          onToggleExpand={handleToggleExpand}
          onAdd={onAdd}
          onRename={onRename}
          onMoveUp={onMoveUp}
          onMoveDown={onMoveDown}
          onDelete={onDelete}
          isSaving={savingIds.has(node.id)}
        />
      );
    });
  };

  if (tree.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
          <Icon name="Menu" size={24} className="text-muted-foreground" />
        </div>
        <p className="text-sm text-muted-foreground">{t('menuSettings.noNodes')}</p>
      </div>
    );
  }

  return <div className="py-1">{renderNodes(tree)}</div>;
};

// Helper: find depth of a node id in the tree
function getDepth(nodes, targetId, currentDepth = 0) {
  for (const n of nodes) {
    if (n.id === targetId) return currentDepth;
    const found = getDepth(n.children || [], targetId, currentDepth + 1);
    if (found >= 0) return found;
  }
  return -1;
}

export default MenuTreeEditor;
